import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { useState } from 'react';
import {
  AlertTriangle,
  ClipboardList,
  Lock,
  LogOut,
  ChevronRight,
} from 'lucide-react';

export default function LogoutBlockedModal() {
  const { 
    user, 
    pendingTasks, 
    logoutBlockedModal, 
    setLogoutBlockedModal, 
    logout,
    canAccess 
  } = useAuth();
  
  const [showOverride, setShowOverride] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');
  const [overriding, setOverriding] = useState(false);

  const handleForceLogout = async () => {
    if (!overrideReason.trim()) {
      return;
    }
    setOverriding(true);
    await logout(true, overrideReason);
    setOverriding(false);
  };

  if (!logoutBlockedModal) return null;

  return (
    <Dialog open={logoutBlockedModal} onOpenChange={setLogoutBlockedModal}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <Lock className="w-5 h-5" />
            Cannot Logout - Pending Tasks
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="p-4 bg-red-50 rounded-lg">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
              <div>
                <p className="font-medium text-red-800">
                  You have {pendingTasks.length} incomplete task{pendingTasks.length > 1 ? 's' : ''}
                </p>
                <p className="text-sm text-red-600 mt-1">
                  Please complete all urgent/high priority tasks before logging out.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-2 max-h-[200px] overflow-y-auto">
            {pendingTasks.map((task) => (
              <Link
                key={task.id}
                to={`/todos`}
                onClick={() => setLogoutBlockedModal(false)}
                className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <ClipboardList className="w-4 h-4 text-slate-500" />
                    <span className="font-medium text-sm">{task.title}</span>
                  </div>
                  {task.candidate_name && (
                    <p className="text-xs text-slate-500 ml-6 mt-0.5">
                      Candidate: {task.candidate_name}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={
                    task.priority === 'urgent' ? 'bg-red-100 text-red-700' :
                    task.priority === 'high' ? 'bg-orange-100 text-orange-700' :
                    'bg-slate-100 text-slate-700'
                  }>
                    {task.priority}
                  </Badge>
                  <ChevronRight className="w-4 h-4 text-slate-400" />
                </div>
              </Link>
            ))}
          </div>

          <div className="flex justify-center">
            <Link
              to="/todos"
              onClick={() => setLogoutBlockedModal(false)}
            >
              <Button variant="default">
                <ClipboardList className="w-4 h-4 mr-2" />
                Go to ToDo List
              </Button>
            </Link>
          </div>

          {/* Senior Override Section */}
          {canAccess('admin') && (
            <div className="border-t pt-4 mt-4">
              {!showOverride ? (
                <Button 
                  variant="outline" 
                  className="w-full text-amber-600 border-amber-300 hover:bg-amber-50"
                  onClick={() => setShowOverride(true)}
                >
                  <Lock className="w-4 h-4 mr-2" />
                  Senior Override (Admin Only)
                </Button>
              ) : (
                <div className="space-y-3 p-3 bg-amber-50 rounded-lg">
                  <p className="text-sm text-amber-800 font-medium">
                    ⚠️ This override will be logged and reported
                  </p>
                  <div className="space-y-2">
                    <Label className="text-amber-700">Reason for Override *</Label>
                    <Input
                      value={overrideReason}
                      onChange={(e) => setOverrideReason(e.target.value)}
                      placeholder="Enter reason for allowing logout..."
                      className="border-amber-300"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setShowOverride(false)}
                    >
                      Cancel
                    </Button>
                    <Button 
                      variant="destructive" 
                      size="sm"
                      onClick={handleForceLogout}
                      disabled={!overrideReason.trim() || overriding}
                    >
                      <LogOut className="w-4 h-4 mr-1" />
                      Force Logout
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setLogoutBlockedModal(false)}>
            Continue Working
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
